import { Link, useLocation } from "wouter";
import { Menu, X } from "lucide-react";
import UserMenu from "@/components/user-menu";
import LanguageSelector from "@/components/language-selector";
import { useTranslation } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import React, { ReactNode, useState } from "react";

// Define the User type based on what we know from user-menu.tsx
interface User {
  avatarUrl?: string;
  cacatais?: string | number;
  email?: string;
  // Add other user properties as needed
}

export default function Header() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [location] = useLocation();
  const { data: user } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
  });

  /* Custom dropdown state and styling */
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  
  // Handle clicking outside to close dropdown
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (activeDropdown && !((event.target as HTMLElement).closest('.dropdown-trigger') || (event.target as HTMLElement).closest('.dropdown-content'))) {
        setActiveDropdown(null);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdown]);

  const toggleDropdown = (name: string) => {
    setActiveDropdown(prev => prev === name ? null : name);
  };

  const NavigationLinks = ({ closeSheet, isInMobileMenu = false }: { closeSheet?: () => void, isInMobileMenu?: boolean }) => (
    <>
     
      {isInMobileMenu ? (
        <li>
          <button 
            onClick={() => toggleDropdown('puzzles')}
            className={`dropdown-trigger flex items-center text-white hover:text-gray-300 transition-all duration-300 ${location === '/puzzles' || location.startsWith('/puzzles/criar-desafios') ? 'font-medium border-b-2 border-red-600' : ''}`}
          >
            {t("header.puzzles")}
            <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'puzzles' ? 'rotate-180' : ''}`} />
          </button>
          {activeDropdown === 'puzzles' && (
            <div className="dropdown-content ml-4 mt-1 space-y-1">
              <Link
                href="/desafios-cacatais"
                className="block py-1 text-white hover:text-gray-300 transition-colors"
                onClick={closeSheet}
              >
                {t("header.custom_puzzles")}
              </Link>
              <Link 
                href="/puzzles"
                className="block py-1 text-white hover:text-gray-300 transition-colors"
                onClick={closeSheet}
              >
                {t("header.puzzles_original")}
              </Link>
              <Link
                href="/puzzles/rank"
                className="block py-1 text-white hover:text-gray-300 transition-colors"
                onClick={closeSheet}
              >
                Rank
              </Link>
              {user && (
                <Link
                  href="/puzzles/discussion"
                  className="block py-1 text-white hover:text-gray-300 transition-colors"
                  onClick={closeSheet}
                >
                  {t("header.discussion") || "Discussion"}
                </Link>
              )}
            </div>
          )}
        </li>
      ) : (
        <li className="relative">
          <button 
            onClick={() => toggleDropdown('puzzles')}
            className={`dropdown-trigger flex items-center text-white hover:text-gray-300 transition-all duration-300 ${location === '/puzzles' || location.startsWith('/puzzles/criar-desafios') ? 'font-medium border-b-2 border-red-600' : ''}`}
          >
            {t("header.puzzles")}
            <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'puzzles' ? 'rotate-180' : ''}`} />
          </button>
          {activeDropdown === 'puzzles' && (
            <div className="dropdown-content absolute top-full left-0 z-50 mt-2 w-[200px] p-4 bg-black/95 rounded-md border border-gray-800 shadow-lg">
              <Link
                href="/desafios-cacatais"
                className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                onClick={() => setActiveDropdown(null)}
              >
                {t("header.custom_puzzles")}
              </Link>
              <Link 
                href="/puzzles"
                className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                onClick={() => setActiveDropdown(null)}
              >
                {t("header.puzzles_original")}
              </Link>
              <Link
                href="/puzzles/rank"
                className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                onClick={() => setActiveDropdown(null)}
              >
                Rank
              </Link>
              {user && (
                <Link
                  href="/puzzles/discussion"
                  className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                  onClick={() => setActiveDropdown(null)}
                >
                  {t("header.discussion") || "Discussion"}
                </Link>
              )}
            </div>
          )}
        </li>
      )}
       {isInMobileMenu ? (
        // Mobile version uses the original NavigationMenu from Radix UI
        <li>
          <button 
            onClick={() => toggleDropdown('pool')}
            className={`dropdown-trigger flex items-center text-white hover:text-gray-300 transition-all duration-300 ${location.startsWith('/pool') ? 'font-medium border-b-2 border-red-600' : ''}`}
          >
            {t("header.pool")}
            <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'pool' ? 'rotate-180' : ''}`} />
          </button>
          {activeDropdown === 'pool' && (
            <div className="dropdown-content ml-4 mt-1 space-y-1">
              <Link 
                href="/pool"
                className="block py-1 text-white hover:text-gray-300 transition-colors"
                onClick={closeSheet}
              >
                {t("header.pool")}
              </Link>
              <Link 
                href="/pool/rank"
                className="block py-1 text-white hover:text-gray-300 transition-colors"
                onClick={closeSheet}
              >
                Rank
              </Link>
            </div>
          )}
        </li>
      ) : (
        // Desktop version uses custom dropdown
        <li className="relative">
          <button 
            onClick={() => toggleDropdown('pool')}
            className={`dropdown-trigger flex items-center text-white hover:text-gray-300 transition-all duration-300 ${location.startsWith('/pool') ? 'font-medium border-b-2 border-red-600' : ''}`}
          >
            {t("header.pool")}
            <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'pool' ? 'rotate-180' : ''}`} />
          </button>
          {activeDropdown === 'pool' && (
            <div className="dropdown-content absolute top-full left-0 z-50 mt-2 w-[200px] p-4 bg-black/95 rounded-md border border-gray-800 shadow-lg">
              <Link 
                href="/pool"
                className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                onClick={() => setActiveDropdown(null)}
              >
                {t("header.pool")}
              </Link>
              <Link 
                href="/pool/rank"
                className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                onClick={() => setActiveDropdown(null)}
              >
                Rank
              </Link>
            </div>
          )}
        </li>
      )}
      {isInMobileMenu ? (
        <li>
          <button 
            onClick={() => toggleDropdown('codes')}
            className={`dropdown-trigger flex items-center text-white hover:text-gray-300 transition-all duration-300 ${location.startsWith('/codes') ? 'font-medium border-b-2 border-red-600' : ''}`}
          >
            Codes
            <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'codes' ? 'rotate-180' : ''}`} />
          </button>
          {activeDropdown === 'codes' && (
            <div className="dropdown-content ml-4 mt-1 space-y-1">
              <Link 
                href="/codes"
                className="block py-1 text-white hover:text-gray-300 transition-colors"
                onClick={closeSheet}
              >
                Codes
              </Link>
              {user && (
                <Link 
                  href="/codes/create"
                  className="block py-1 text-white hover:text-gray-300 transition-colors"
                  onClick={closeSheet}
                >
                  Add New Code
                </Link>
              )}
            </div>
          )}
        </li>
      ) : (
        <li className="relative">
          <button 
            onClick={() => toggleDropdown('codes')}
            className={`dropdown-trigger flex items-center text-white hover:text-gray-300 transition-all duration-300 ${location.startsWith('/codes') ? 'font-medium border-b-2 border-red-600' : ''}`}
          >
            Codes
            <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'codes' ? 'rotate-180' : ''}`} />
          </button>
          {activeDropdown === 'codes' && (
            <div className="dropdown-content absolute top-full left-0 z-50 mt-2 w-[200px] p-4 bg-black/95 rounded-md border border-gray-800 shadow-lg">
              <Link 
                href="/codes"
                className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                onClick={() => setActiveDropdown(null)}
              >
                Codes
              </Link>
              {user && (
                <Link 
                  href="/codes/create"
                  className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                  onClick={() => setActiveDropdown(null)}
                >
                  Add New Code
                </Link>
              )}
            </div>
          )}
        </li>
      )}
      <li>
        <Link 
          href="/cacatal" 
          className={`block text-white hover:text-gray-300 transition-all duration-300 ${location === '/cacatal' ? 'font-medium border-b-2 border-red-600' : ''}`}
          onClick={isInMobileMenu ? closeSheet : undefined}
        >
          Caçatal
        </Link>
      </li>
      <li>
        <Link 
          href="/faq" 
          className={`block text-white hover:text-gray-300 transition-all duration-300 ${location === '/faq' ? 'font-medium border-b-2 border-red-600' : ''}`}
          onClick={isInMobileMenu ? closeSheet : undefined}
        >
          {t("header.faq")}
        </Link>
      </li>
      <li>
        <a 
          href="https://lmajowka.github.io/webloteria/" 
          className={`block text-white hover:text-gray-300 transition-all duration-300`}
          onClick={isInMobileMenu ? closeSheet : undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("header.webloteria")}
        </a>
      </li>
      {/* Special menu - only for desktop and when not in production or specific user */}
      {!isInMobileMenu && (user?.email === "leo.majowka@gmail.com" || import.meta.env.MODE !== "production") && (
        <li className="relative">
          <button 
            onClick={() => toggleDropdown('special')}
            className={`dropdown-trigger flex items-center text-white hover:text-gray-300 transition-all duration-300 ${location.startsWith('/special') ? 'font-medium border-b-2 border-red-600' : ''}`}
          >
            Special
            <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${activeDropdown === 'special' ? 'rotate-180' : ''}`} />
          </button>
          {activeDropdown === 'special' && (
            <div className="dropdown-content absolute top-full left-0 z-50 mt-2 w-[200px] p-4 bg-black/95 rounded-md border border-gray-800 shadow-lg">
              <Link 
                href="/api/test/signin/1"
                className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                onClick={() => setActiveDropdown(null)}
              >
                Login user 1
              </Link>
              <Link 
                href="/api/test/signin/2"
                className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                onClick={() => setActiveDropdown(null)}
              >
                Login user 2
              </Link>
              <Link 
                href="/admin/videos"
                className="block p-2 hover:bg-sidebar-accent rounded-md text-white hover:text-gray-300 transition-colors"
                onClick={() => setActiveDropdown(null)}
              >
                Admin
              </Link>
            </div>
          )}
        </li>
      )}
    </>
  );


  return (
    <header className="fixed w-full z-50 bg-gradient-to-b from-black via-black/80 to-transparent px-3 sm:px-6 py-3 sm:py-4 backdrop-blur-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 sm:gap-8">
          <Link 
            href="/" 
            style={{"margin-bottom": "4px"}}
            className={`text-xl sm:text-2xl font-bold transform transition-transform duration-300 hover:scale-105 ${location === '/' ? 'scale-105' : ''}`}
          >
            <span className="text-red-600">Bitcoin</span>
            <span className="text-white">Puzzles</span>
          </Link>
          {/* Desktop Navigation */}
          {!isMobile && (
            <nav>
              <ul className="flex items-center gap-6 sm:gap-8">
                <NavigationLinks />
              </ul>
            </nav>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          {/* Mobile Menu */}
          {isMobile && (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 bg-black/95 border-r border-white/10">
                <div className="sticky top-0 flex items-center justify-between p-4 bg-gradient-to-b from-black to-black/90">
                  <Link href="/" className="text-xl font-bold">
                    <span className="text-red-600">Bitcoin</span>
                    <span className="text-white">Puzzles</span>
                  </Link>
                  <SheetClose className="rounded-full p-1 hover:bg-white/10" />
                </div>
                <nav className="flex flex-col gap-2 p-4">
                  <ul className="flex flex-col items-start gap-3 w-full">
                    <NavigationLinks 
                      isInMobileMenu={true}
                      closeSheet={() => {
                        const closeButton = document.querySelector('[data-sheet-close]') as HTMLElement;
                        if (closeButton) closeButton.click();
                      }} 
                    />
                  </ul>
                  <div className="mt-6 pt-4 border-t border-white/10">
                    <div className="flex justify-start">
                      <LanguageSelector />
                    </div>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          )}
          {/* Desktop Language Selector */}
          {!isMobile && <LanguageSelector />}
          {/* Display Cacatais Balance when user is signed in */}
          {user && (
            <div className="flex items-center gap-1 px-2 py-1 bg-black/30 rounded-md border border-amber-500/30">
              <span className="text-amber-500 font-medium">{Number(user.cacatais).toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
              <span className="text-xs text-amber-400">Caçatais</span>
            </div>
          )}
          <UserMenu />
        </div>
      </div>
    </header>
  );
}